import {Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EChartsOption} from "echarts";
import {
    EntityPerformanceChartComponent,
    PerformanceRecord
} from "../entity-performance-chart/entity-performance-chart.component";
import {
    Connection,
    ConnectionType,
    DataType,
    GetMeterTreeData,
    Location,
    MeterTreeMeasurementsResult,
    MeterType,
    TimeResolution
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {
    DataQueryFilters,
    MeasurementsHandlerOpenModalCommand
} from "../../measurements-component/measurements-handler.component";
import {Entity, EntityType} from "../../../handlers/entity";
import {ChartDataPerMeasurement, DashboardTime} from "../../../utils/chart-data-provider";
import {catchError, debounceTime, EMPTY, Observable, of, skip, Subject, switchMap} from "rxjs";
import {Measurements} from "../../measurements-component/measurements";
import {lodash} from "../../../common/utils";
import {MeterMetadataField, RefdataUtils} from "../../refdata/refdata-utils";
import {AppCommonUtils, sendQuery} from "../../../common/app-common-utils";
import {SourcesProvider} from "../../../utils/source-providers/sources-provider";
import {AppContext} from "../../../app-context";
import {OrganisationProvider} from "../../../utils/source-providers/organisation-provider";
import {ChartOptionType, MeterTreeName} from "../../dashboard/dashboard.types";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {MiddleEllipsisPipe} from "../../../common/middle-ellipsis.pipe";
import {downloadSubmeterData} from "../../refdata/connections/connections-overview/bulk-data-download";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {
    buildMeterDisplayHierarchy,
    CALCULATED_RESIDUAL_COLOR,
    CALCULATED_RESIDUAL_METER_PREFIX,
    calculatedResidualEntity,
    effectiveMeterType,
    isCalculatedResidual,
    isChargePointMeter,
    isMeterVisible
} from '../../refdata/meters/submeter-role.utils';
import {meterTreeHasData} from './meter-tree-data-state';
import {
    responsiveTreeLayout,
    TREE_CARD_HEIGHT,
    TREE_CARD_MAX_WIDTH,
    updateResponsiveTreeLabelWidth
} from '../tree-chart/tree-chart.component';
import {
    defaultIntermediateConsumptionType,
    hasEligibleMeterTreeMeters,
    intermediateConsumptionTypeForPeriod,
    intermediateConsumptionTypesWithData,
    linkedElectricityConnectionId,
    meterTreeName,
    meterTreeNameSelectable,
    splitFilterValues
} from '../intermediate-meter.utils';
import {MeterTreeNamePreferenceService} from '../meter-tree-name-preference.service';

export interface MeterFilterEntry {
    category: string;
    value: string;
}

export interface MeterTreeTableRow {
    key: string;
    name: string;
    parentName: string | null;
    level: number;
    value: number;
    shareOfMainMeter: number;
    shareOfParent: number | null;
    overlay: boolean;
    measurementType?: DataType;
}

export interface MeterTreeNode {
    id?: string;
    entityId?: string;
    connectionId?: string;
    sourceConnectionIds?: string[];
    name: string;
    value: number;
    flowValue?: number;
    parentFlowValue?: number;
    depth?: number;
    children: MeterTreeNode[];
    measurementType?: DataType;
    calculatedResidual?: boolean;
    historical?: boolean;
    overlay?: boolean;
    aggregate?: boolean;
    overAllocated?: boolean;
    scaled?: boolean;
    showDetails?: boolean;
    active?: boolean;
    selected?: boolean;
    collapsed?: boolean;
    symbol?: string;
    lineStyle?: Record<string, unknown>;
    itemStyle?: Record<string, unknown>;
    emphasis?: Record<string, unknown>;
    cardBorderColor?: string;
    cardBorderWidth?: number;
}

const STANDARD_FILTER_CATEGORIES = new Set(['tenant', 'floor', 'tower', 'wing']);
const CHARGE_POINT_GROUP_PREFIX = 'charge-point-group-';
const FLOW_LINE_WIDTH = 18;
const MIN_FLOW_LINE_WIDTH = 1;
const FLOW_COLOR = '#4f7fe8';
const HISTORICAL_COLOR = '#94a3b8';
const OVERLAY_COLOR = '#7c5ce7';
const RESIDUAL_COLOR = CALCULATED_RESIDUAL_COLOR;
const ACTIVE_CARD_FILL = '#e6efff';
const ACTIVE_CARD_BORDER = '#3974e8';
export const METER_TREE_REVEAL_SHIFT = 24;
const METER_TREE_CARD_SYMBOL = 'path://M12 0H230C236.627 0 242 5.373 242 12V36C242 42.627 236.627 48 230 48H12C5.373 48 0 42.627 0 36V12C0 5.373 5.373 0 12 0Z';
const METER_TREE_STACK_CARD_SYMBOL = 'path://M12 0H230C236.627 0 242 5.373 242 12V48H0V12C0 5.373 5.373 0 12 0Z';
const METER_TREE_LANE_REVEAL_GAP = 24;
const METER_TREE_LANE_ANIMATION_DURATION = 360;
const METER_TREE_LANE_ANIMATION_EASING = 'cubicInOut';
const METER_TREE_LANE_EDGE_EXIT_DURATION = 140;
const METER_TREE_LABEL_HORIZONTAL_PADDING = 18;
const METER_TREE_PERCENT_WIDTH = 42;
const METER_TREE_PERCENT_NAME_GAP = 7;
const METER_TREE_BRANCH_WIDTH = 16;
const METER_TREE_WARNING_WIDTH = 21;
const METER_TREE_NAME_SAFETY_MARGIN = 3;
let meterTreeMeasurementContext: CanvasRenderingContext2D | null | undefined;

function buildMeterTreeCardBorderPath(path: any, shape: any): void {
    const {x, y, width, height, radius, roundBottom} = shape;
    path.moveTo(x + radius, y);
    path.lineTo(x + width - radius, y);
    path.arc(x + width - radius, y + radius, radius, -Math.PI / 2, 0);
    path.lineTo(x + width, y + height - (roundBottom ? radius : 0));
    if (!roundBottom) {
        path.moveTo(x, y + height);
        path.lineTo(x, y + radius);
        path.arc(x + radius, y + radius, radius, Math.PI, Math.PI * 1.5);
        return;
    }
    path.arc(x + width - radius, y + height - radius, radius, 0, Math.PI / 2);
    path.lineTo(x + radius, y + height);
    path.arc(x + radius, y + height - radius, radius, Math.PI / 2, Math.PI);
    path.lineTo(x, y + radius);
    path.arc(x + radius, y + radius, radius, Math.PI, Math.PI * 1.5);
    path.closePath();
}

export function meterTreeTableRows(tree: any): MeterTreeTableRow[] {
    if (!tree) return [];
    const rootValue = tree.value || 0;
    const rows: MeterTreeTableRow[] = [];
    const addNode = (node: MeterTreeNode, parent: MeterTreeNode | null, level: number) => {
        const parentName = parent?.name || null;
        const value = node.flowValue ?? node.value ?? 0;
        const parentValue = parent ? parent.flowValue ?? parent.value ?? 0 : 0;
        const row: MeterTreeTableRow = {
            key: node.id || node.entityId || node.connectionId || `${level}-${parentName || 'root'}-${node.name}`,
            name: node.name,
            parentName,
            level,
            value: node.value || 0,
            shareOfMainMeter: level === 0 ? 100 : rootValue ? value / rootValue * 100 : 0,
            shareOfParent: parent ? parentValue ? value / parentValue * 100 : 0 : null,
            overlay: !!node.overlay
        };
        if (node.measurementType) row.measurementType = node.measurementType;
        rows.push(row);
        (node.children || []).forEach(child => addNode(child, node, level + 1));
    };
    addNode(tree, null, 0);
    return rows;
}

export function meterTreeOverlayIds(topLevelRecords: PerformanceRecord[]): Set<string> {
    const candidates = topLevelRecords.filter(record =>
        !record.entityId.startsWith(CALCULATED_RESIDUAL_METER_PREFIX)
        && !record.entityId.startsWith(CHARGE_POINT_GROUP_PREFIX));
    if (!candidates.some(record => !record.virtual)) return new Set();
    return new Set(candidates.filter(record => record.virtual).map(record => record.entityId));
}

export function physicalMeterTreeRecords(topLevelRecords: PerformanceRecord[]): PerformanceRecord[] {
    const overlayIds = meterTreeOverlayIds(topLevelRecords);
    return topLevelRecords.filter(record => !overlayIds.has(record.entityId)
        && !record.entityId.startsWith(CALCULATED_RESIDUAL_METER_PREFIX));
}

export function allocateMeterTreeFlows(node: MeterTreeNode, availableFlow = node.value,
                                       parentFlowValue?: number, depth = 0): MeterTreeNode {
    node.flowValue = Math.max(availableFlow || 0, 0);
    node.parentFlowValue = parentFlowValue;
    node.depth = depth;
    const additiveChildren = (node.children || []).filter(child => !child.overlay);
    const measuredChildTotal = lodash.sum(additiveChildren.map(child => Math.max(child.value || 0, 0)));
    const tolerance = Math.max(0.01, node.value * 0.000001);
    node.scaled = !node.overlay && node.value > 0 && node.flowValue < node.value - tolerance;
    node.overAllocated = measuredChildTotal > node.value + tolerance;
    const scale = measuredChildTotal > node.flowValue && measuredChildTotal > 0
        ? node.flowValue / measuredChildTotal : 1;
    (node.children || []).forEach(child => {
        const childFlow = child.overlay ? child.value : Math.max(child.value || 0, 0) * scale;
        allocateMeterTreeFlows(child, childFlow, node.flowValue, depth + 1);
    });
    return node;
}

export function markMeterTreeCardDetails(node: MeterTreeNode, showDetails = true): MeterTreeNode {
    node.showDetails = showDetails;
    node.symbol = showDetails ? METER_TREE_CARD_SYMBOL : METER_TREE_STACK_CARD_SYMBOL;
    const children = node.children || [];
    children.forEach((child, index) => markMeterTreeCardDetails(child, index === children.length - 1));
    return node;
}

export function meterTreeRevealShift(lowerSiblingIndex: number, lowerSiblingCount: number,
                                     maximumShift = METER_TREE_REVEAL_SHIFT): number {
    if (lowerSiblingCount <= 0 || lowerSiblingIndex < 0 || lowerSiblingIndex >= lowerSiblingCount) return 0;
    return maximumShift;
}

export function meterTreeBranchMarker(active: boolean): string {
    return active ? '‹' : '›';
}

function animateMeterTreeCard(symbol: any, offsetY: number): void {
    const currentOffset = symbol?.meterTreeRevealOffsetY ?? 0;
    if (currentOffset === offsetY) return;
    const keepStoredLayout = (symbol?.meterTreeRevealAnimating || currentOffset !== 0)
        && symbol?.meterTreeLayoutY !== undefined;
    const layoutY = keepStoredLayout ? symbol.meterTreeLayoutY : (symbol?.y ?? 0) - currentOffset;
    const animationId = (symbol?.meterTreeRevealAnimationId ?? 0) + 1;
    symbol.meterTreeLayoutY = layoutY;
    symbol.meterTreeRevealOffsetY = offsetY;
    symbol.meterTreeRevealAnimationId = animationId;
    symbol.meterTreeRevealAnimating = true;
    symbol?.stopAnimation?.('meter-tree-card-reveal');
    if (symbol?.animateTo) {
        symbol.animateTo({y: layoutY + offsetY}, {
            duration: 160,
            easing: 'cubicOut',
            scope: 'meter-tree-card-reveal',
            done: () => {
                if (symbol.meterTreeRevealAnimationId !== animationId) return;
                symbol.meterTreeRevealAnimating = false;
                symbol.attr?.({y: layoutY + offsetY});
            }
        });
    } else {
        symbol?.attr?.({y: layoutY + offsetY});
        symbol.meterTreeRevealAnimating = false;
    }
}

export function revealMeterTreeCardInDeck(chart: any, dataIndex: number, reveal: boolean): void {
    const data = chart?.getModel?.()?.getSeriesByIndex?.(0)?.getData?.();
    const node = data?.tree?.getNodeByDataIndex?.(dataIndex);
    const siblings = node?.parentNode?.children || [];
    const nodeIndex = siblings.indexOf(node);
    const lowerSiblings = reveal && nodeIndex >= 0 ? siblings.slice(nodeIndex + 1) : [];
    const shiftByDataIndex = new Map<number, number>(lowerSiblings.map((sibling: any, index: number): [number, number] => [
        sibling.dataIndex, meterTreeRevealShift(index, lowerSiblings.length)
    ]));
    data?.eachItemGraphicEl?.((symbol: any, currentDataIndex: number) => {
        animateMeterTreeCard(symbol, shiftByDataIndex.get(currentDataIndex) ?? 0);
    });
}

export function resetMeterTreeCardDeck(chart: any): void {
    const data = chart?.getModel?.()?.getSeriesByIndex?.(0)?.getData?.();
    data?.eachItemGraphicEl?.((symbol: any) => {
        const currentOffset = symbol?.meterTreeRevealOffsetY ?? 0;
        if (currentOffset === 0 && !symbol?.meterTreeRevealAnimating) {
            symbol.meterTreeLayoutY = symbol?.y ?? 0;
            return;
        }
        const layoutY = symbol?.meterTreeLayoutY ?? (symbol?.y ?? 0) - currentOffset;
        symbol.meterTreeRevealAnimationId = (symbol?.meterTreeRevealAnimationId ?? 0) + 1;
        symbol.meterTreeRevealOffsetY = 0;
        symbol.meterTreeRevealAnimating = false;
        symbol.meterTreeLayoutY = layoutY;
        symbol?.stopAnimation?.('meter-tree-card-reveal');
        symbol?.attr?.({y: layoutY});
    });
}

export function finishMeterTreeLaneReveal(chart: any): void {
    const data = chart?.getModel?.()?.getSeriesByIndex?.(0)?.getData?.();
    data?.eachItemGraphicEl?.((symbol: any) => {
        // A second click may replace the tree while the custom horizontal lane
        // reveal is still running. Finish that reveal first so ECharts alone
        // owns the following collapse animation; otherwise both animations
        // mutate the same symbols and leave duplicate/warped exit graphics.
        symbol?.stopAnimation?.('meter-tree-lane-reveal', true);
    });
    (chart?.meterTreeExitingSymbols || []).forEach((symbol: any) => {
        symbol?.stopAnimation?.('meter-tree-lane-exit', true);
        symbol?.parent?.remove?.(symbol);
    });
    (chart?.meterTreeExitingEdges || []).forEach((edge: any) => {
        edge?.stopAnimation?.('meter-tree-lane-exit-edge', true);
        edge?.parent?.remove?.(edge);
    });
    if (chart) chart.meterTreeExitingSymbols = [];
    if (chart) chart.meterTreeExitingEdges = [];
    // Tree edges have their own ECharts/ZRender animators and are not children
    // of the card symbols. Cancel those too before the next setOption; otherwise
    // a rapid close can leave the previous opening lines drawing over the new
    // collapsed state.
    chart?.getZr?.()?.animation?.clear?.();
}

function setMeterTreeElementSilent(element: any, silent: boolean): void {
    if (!element) return;
    if (silent) {
        if (element.meterTreeOriginalSilent === undefined) {
            element.meterTreeOriginalSilent = !!element.silent;
        }
        element.silent = true;
        return;
    }
    if (element.meterTreeOriginalSilent === undefined) return;
    element.silent = element.meterTreeOriginalSilent;
    delete element.meterTreeOriginalSilent;
}

export function setMeterTreeGraphicsSilent(chart: any, silent: boolean): void {
    const data = chart?.getModel?.()?.getSeriesByIndex?.(0)?.getData?.();
    data?.eachItemGraphicEl?.((symbol: any) => {
        setMeterTreeElementSilent(symbol, silent);
        symbol?.traverse?.((element: any) => setMeterTreeElementSilent(element, silent));
        setMeterTreeElementSilent(symbol?.__edge, silent);
    });
}

export function createMeterTreeDeckController(chart: any) {
    let revealedDataIndex: number | undefined;
    let suspended = false;
    let suspensionGeneration = 0;
    const downplay = (dataIndex?: number) => chart?.dispatchAction?.({
        type: 'downplay',
        seriesIndex: 0,
        ...(dataIndex === undefined ? {} : {dataIndex})
    });
    const suppressHover = (dataIndex?: number) => {
        downplay(dataIndex);
        chart?.dispatchAction?.({type: 'hideTip'});
    };
    const close = () => {
        if (revealedDataIndex === undefined) {
            downplay();
            return;
        }
        const closingDataIndex = revealedDataIndex;
        revealMeterTreeCardInDeck(chart, closingDataIndex, false);
        revealedDataIndex = undefined;
        downplay(closingDataIndex);
    };
    return {
        reveal(dataIndex: number) {
            if (suspended) {
                suppressHover(dataIndex);
                return;
            }
            if (revealedDataIndex === dataIndex) return;
            if (revealedDataIndex !== undefined) downplay(revealedDataIndex);
            revealMeterTreeCardInDeck(chart, dataIndex, true);
            revealedDataIndex = dataIndex;
        },
        conceal(dataIndex: number) {
            if (suspended) {
                suppressHover(dataIndex);
                return;
            }
            if (revealedDataIndex !== dataIndex) return;
            close();
        },
        close,
        reset() {
            downplay();
            resetMeterTreeCardDeck(chart);
            revealedDataIndex = undefined;
        },
        suspend(): number {
            suspended = true;
            suspensionGeneration += 1;
            suppressHover();
            chart?.getZr?.()?.setCursorStyle?.('default');
            finishMeterTreeLaneReveal(chart);
            resetMeterTreeCardDeck(chart);
            revealedDataIndex = undefined;
            return suspensionGeneration;
        },
        resume(generation: number): boolean {
            if (generation !== suspensionGeneration) return false;
            downplay();
            resetMeterTreeCardDeck(chart);
            revealedDataIndex = undefined;
            suspended = false;
            setMeterTreeGraphicsSilent(chart, false);
            return true;
        }
    };
}

export function resumeMeterTreeInteractionsAfterUpdate(chart: any, deckController: any,
                                                       generation: number): void {
    const onFinished = () => {
        chart?.off?.('finished', onFinished);
        keepMeterTreeCardsInDeck(chart);
        deckController?.resume?.(generation);
    };
    chart?.on?.('finished', onFinished);
}

export function keepMeterTreeCardsInDeck(chart: any): void {
    const data = chart?.getModel?.()?.getSeriesByIndex?.(0)?.getData?.();
    data?.eachItemGraphicEl?.((symbol: any, dataIndex: number) => {
        if (!symbol?.meterTreeRevealAnimating) {
            symbol.meterTreeLayoutY = (symbol?.y ?? 0) - (symbol?.meterTreeRevealOffsetY ?? 0);
        }
        const card = symbol?.childAt?.(0);
        const node = data?.getRawDataItem?.(dataIndex) as MeterTreeNode | undefined;
        let border = symbol?.meterTreeCardBorder;
        if (card && node) {
            if (!border) {
                border = new card.constructor({silent: true});
                border.buildPath = buildMeterTreeCardBorderPath;
                symbol.add(border);
                symbol.meterTreeCardBorder = border;
            }
            const width = (card.scaleX || 0) * 2;
            const height = (card.scaleY || 0) * 2;
            border.setShape({
                x: -width / 2,
                y: -height / 2,
                width,
                height,
                radius: 12,
                roundBottom: !!node.showDetails
            });
            border.useStyle({
                fill: null,
                stroke: node.cardBorderColor || '#dce3ec',
                lineWidth: node.cardBorderWidth || 1,
                strokeNoScale: true
            });
            border.z2 = card.z2 + 0.1;
        }
        [symbol, card, border, card?.getTextContent?.()].filter(Boolean).forEach((element: any) => {
            element.z2EmphasisLift = 0;
            const emphasisState = element.ensureState?.('emphasis');
            if (emphasisState) emphasisState.z2 = element.z2;
        });
    });
}

function meterTreeNodeKey(node: MeterTreeNode): string {
    return node.id || node.entityId || node.connectionId || node.name;
}

export function animateMeterTreeExpandedLane(chart: any, expandedNode: MeterTreeNode): void {
    const childKeys = new Set((expandedNode.children || []).map(meterTreeNodeKey));
    if (!childKeys.size) return;
    const data = chart?.getModel?.()?.getSeriesByIndex?.(0)?.getData?.();
    data?.eachItemGraphicEl?.((symbol: any, dataIndex: number) => {
        const node = data?.getRawDataItem?.(dataIndex) as MeterTreeNode | undefined;
        const layout = data?.getItemLayout?.(dataIndex);
        if (!node || !childKeys.has(meterTreeNodeKey(node)) || !layout) return;
        // ECharts enters new tree nodes at their parent's x/y. Siblings therefore
        // overlap until the update animation has fanned them out. Finish every
        // animation inside the symbol before taking ownership of the group: text,
        // card and border otherwise keep following their native transition while
        // the group is already moving horizontally.
        symbol?.stopAnimation?.(undefined, true);
        symbol?.traverse?.((element: any) => element?.stopAnimation?.(undefined, true));
        symbol.meterTreeLayoutY = layout.y;
        symbol.meterTreeRevealOffsetY = 0;
        symbol.meterTreeRevealAnimating = false;
        const card = symbol?.childAt?.(0);
        const cardWidth = Math.abs(card?.scaleX || TREE_CARD_MAX_WIDTH / 2) * 2;
        symbol?.attr?.({x: layout.x + cardWidth + METER_TREE_LANE_REVEAL_GAP, y: layout.y});
        if (symbol?.animateTo) {
            symbol.animateTo({x: layout.x, y: layout.y}, {
                duration: METER_TREE_LANE_ANIMATION_DURATION,
                easing: METER_TREE_LANE_ANIMATION_EASING,
                scope: 'meter-tree-lane-reveal'
            });
        } else {
            symbol?.attr?.({x: layout.x, y: layout.y});
        }
    });
}

interface MeterTreeLaneExitSymbol {
    symbol: any;
    edge: any;
    x: number;
    y: number;
    exitX: number;
}

export function captureMeterTreeCollapsingLane(chart: any, collapsingNode: MeterTreeNode): MeterTreeLaneExitSymbol[] {
    const descendantKeys = new Set<string>();
    const collectDescendantKeys = (node: MeterTreeNode) => (node.children || []).forEach(child => {
        descendantKeys.add(meterTreeNodeKey(child));
        collectDescendantKeys(child);
    });
    collectDescendantKeys(collapsingNode);
    if (!descendantKeys.size) return [];

    const captured: Array<MeterTreeLaneExitSymbol & {cardWidth: number}> = [];
    const data = chart?.getModel?.()?.getSeriesByIndex?.(0)?.getData?.();
    data?.eachItemGraphicEl?.((symbol: any, dataIndex: number) => {
        const node = data?.getRawDataItem?.(dataIndex) as MeterTreeNode | undefined;
        if (!node || !descendantKeys.has(meterTreeNodeKey(node))) return;
        const card = symbol?.childAt?.(0);
        captured.push({
            symbol,
            edge: symbol?.__edge,
            x: symbol?.x ?? 0,
            y: symbol?.y ?? 0,
            exitX: 0,
            cardWidth: Math.abs(card?.scaleX || TREE_CARD_MAX_WIDTH / 2) * 2
        });
    });
    if (!captured.length) return [];

    // Keep every disappearing lane at the same distance from its neighbours.
    // The leftmost exiting deck ends one card width beyond the former rightmost
    // deck, so the surviving parent can reflow right without crossing it.
    const leftmostX = Math.min(...captured.map(item => item.x));
    const rightmostX = Math.max(...captured.map(item => item.x));
    const cardWidth = Math.max(...captured.map(item => item.cardWidth));
    const shift = rightmostX - leftmostX + cardWidth + METER_TREE_LANE_REVEAL_GAP;
    return captured.map(({cardWidth: _cardWidth, ...item}) => ({...item, exitX: item.x + shift}));
}

export function animateMeterTreeCollapsedLane(chart: any, exitingSymbols: MeterTreeLaneExitSymbol[]): void {
    if (!exitingSymbols.length) return;
    chart.meterTreeExitingSymbols = exitingSymbols.map(item => item.symbol);
    const exitingEdges = Array.from(new Set(exitingSymbols.map(item => item.edge).filter(Boolean)));
    chart.meterTreeExitingEdges = exitingEdges;
    exitingEdges.forEach((edge: any) => {
        edge?.stopAnimation?.(undefined, false);
        const removeEdge = () => {
            edge?.parent?.remove?.(edge);
            chart.meterTreeExitingEdges = (chart.meterTreeExitingEdges || [])
                .filter((candidate: any) => candidate !== edge);
        };
        if (edge?.animateTo) {
            edge.animateTo({style: {opacity: 0}}, {
                duration: METER_TREE_LANE_EDGE_EXIT_DURATION,
                easing: 'cubicOut',
                scope: 'meter-tree-lane-exit-edge',
                done: removeEdge
            });
        } else {
            removeEdge();
        }
    });
    exitingSymbols.forEach(({symbol, x, y, exitX}) => {
        // ECharts normally collapses removed tree nodes into their parent while
        // shrinking and fading them. Stop that leave animation at its first
        // frame and move the complete card group outwards instead.
        symbol?.stopAnimation?.(undefined, false);
        symbol?.traverse?.((element: any) => element?.stopAnimation?.(undefined, false));
        symbol?.attr?.({x, y});
        const remove = () => {
            symbol?.parent?.remove?.(symbol);
            chart.meterTreeExitingSymbols = (chart.meterTreeExitingSymbols || [])
                .filter((candidate: any) => candidate !== symbol);
        };
        if (symbol?.animateTo) {
            symbol.animateTo({x: exitX, y}, {
                duration: METER_TREE_LANE_ANIMATION_DURATION,
                easing: METER_TREE_LANE_ANIMATION_EASING,
                scope: 'meter-tree-lane-exit',
                done: remove
            });
        } else {
            remove();
        }
    });
}

export function meterTreePercentage(value: number, rootValue: number): string {
    const percentage = rootValue > 0 ? Math.max(value || 0, 0) / rootValue * 100 : 0;
    return percentage > 0 && percentage < 0.1 ? '<0.1%' : `${percentage.toFixed(1)}%`;
}

export function meterTreeCardFill(node: MeterTreeNode, active: boolean,
                                  siblingIndex: number, depth: number): string {
    if (active) return ACTIVE_CARD_FILL;
    if (node.connectionId) return '#f5f8fd';
    const alternating = depth >= 2 && siblingIndex % 2 === 1;
    if (node.overlay) return alternating ? '#f3f0fc' : '#f8f6ff';
    if (node.calculatedResidual) return alternating ? '#f5f2fc' : '#faf8ff';
    if (node.historical) return alternating ? '#f1f4f8' : '#f7f9fc';
    return alternating ? '#f4f7fc' : '#ffffff';
}

export function initializeMeterTreeExpansion(node: MeterTreeNode, depth = 0): void {
    const hasChildren = !!node.children?.length;
    node.collapsed = depth > 0;
    node.active = depth === 0 && hasChildren;
    node.selected = depth === 0 && hasChildren;
    (node.children || []).forEach(child => initializeMeterTreeExpansion(child, depth + 1));
}

export function selectMeterTreePath(root: MeterTreeNode, selectedPath: MeterTreeNode[]): void {
    const selectedNodes = new Set(selectedPath);
    const updateInteractionState = (node: MeterTreeNode) => {
        node.active = false;
        node.selected = selectedNodes.has(node);
        (node.children || []).forEach(updateInteractionState);
    };
    updateInteractionState(root);
}

export function meterTreeCardSelected(node: MeterTreeNode): boolean {
    return !!node.selected && !node.connectionId;
}

export function meterTreeNameCharacterCount(node: MeterTreeNode): number {
    if (node.overAllocated && node.children?.length) return 8;
    if (node.overAllocated) return 9;
    if (node.children?.length) return 11;
    return 13;
}

export function meterTreeAvailableNameWidth(node: MeterTreeNode, cardWidth = TREE_CARD_MAX_WIDTH): number {
    const reservedWidth = METER_TREE_LABEL_HORIZONTAL_PADDING + METER_TREE_PERCENT_WIDTH
        + METER_TREE_PERCENT_NAME_GAP
        + (node.children?.length ? METER_TREE_BRANCH_WIDTH : 0)
        + (node.overAllocated ? METER_TREE_WARNING_WIDTH : 0)
        + METER_TREE_NAME_SAFETY_MARGIN;
    return Math.max(12, cardWidth - reservedWidth);
}

function meterTreeNameFont(node: MeterTreeNode): string {
    if (node.connectionId) return '650 12.5px sans-serif';
    if (node.overlay) return '620 12px sans-serif';
    if (node.historical) return '550 12px sans-serif';
    return '560 12px sans-serif';
}

export function measureMeterTreeTextWidth(text: string, font: string): number {
    if (meterTreeMeasurementContext === undefined) {
        meterTreeMeasurementContext = typeof document === 'undefined'
            ? null : document.createElement('canvas').getContext('2d');
    }
    if (!meterTreeMeasurementContext) return text.length * 6.5;
    meterTreeMeasurementContext.font = font;
    return meterTreeMeasurementContext.measureText(text).width;
}

export function fitMeterTreeName(node: MeterTreeNode, cardWidth = TREE_CARD_MAX_WIDTH): string {
    const name = node.name || '';
    const availableWidth = meterTreeAvailableNameWidth(node, cardWidth);
    const font = meterTreeNameFont(node);
    if (measureMeterTreeTextWidth(name, font) <= availableWidth) return name;

    let lowerBound = 1;
    let upperBound = Math.min(meterTreeNameCharacterCount(node), Math.floor((name.length - 1) / 2));
    let bestFit = '…';
    while (lowerBound <= upperBound) {
        const characterCount = Math.floor((lowerBound + upperBound) / 2);
        const candidate = MiddleEllipsisPipe.format(name, characterCount);
        if (measureMeterTreeTextWidth(candidate, font) <= availableWidth) {
            bestFit = candidate;
            lowerBound = characterCount + 1;
        } else {
            upperBound = characterCount - 1;
        }
    }
    return bestFit;
}

function escapeMeterTreeHtml(value: string): string {
    const entities: Record<string, string> = {
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    };
    return (value || '').replace(/[&<>'"]/g, character => entities[character]);
}

export function meterFilterEntries(entity: Entity): MeterFilterEntry[] {
    const standardEntries: MeterFilterEntry[] = [
        ...splitFilterValues(RefdataUtils.getMeterMetadataValue(entity.meter, 'tenant')).map(value => ({category: 'tenant', value})),
        ...singleFilterValue('floor', RefdataUtils.getMeterMetadataValue(entity.meter, 'floor')),
        ...singleFilterValue('tower', RefdataUtils.getMeterMetadataValue(entity.meter, 'tower')),
        ...singleFilterValue('wing', RefdataUtils.getMeterMetadataValue(entity.meter, 'wing'))
    ];
    const tagEntries: MeterFilterEntry[] = [];
    RefdataUtils.getMeterTags(entity.meter).forEach(tag => {
        const category = tag.category === 'custom' ? tag.customType : tag.category;
        if (category && tag.value && !isStandardFilterTag(tag)) {
            tagEntries.push({category, value: tag.value});
        }
    });
    return [...standardEntries, ...tagEntries].filter((entry, index, entries) =>
        entries.findIndex(candidate => candidate.category === entry.category && candidate.value === entry.value) === index);
}

export function meterMatchesFilters(entity: Entity, filters: Record<string, string | null>): boolean {
    const entries = meterFilterEntries(entity);
    return Object.entries(filters || {}).every(([category, value]) =>
        !value || entries.some(entry => entry.category === category && entry.value === value));
}

export function reconcileSelectedMeterFilters(selectedFilters: Record<string, string | null>,
                                              availableFilters: Record<string, string[]>): Record<string, string | null> {
    return Object.entries(selectedFilters || {}).reduce((result, [category, value]) => {
        if (value && availableFilters[category]?.includes(value)) {
            result[category] = value;
        }
        return result;
    }, {} as Record<string, string | null>);
}

export function connectionTreeName(connection: Connection): string {
    return connection?.info?.label?.trim() || RefdataUtils.connectionFormatter(connection);
}

export function meterTreeHasRelevantData(location: Location | null | undefined,
                                         result: MeterTreeMeasurementsResult | null | undefined): boolean {
    return intermediateConsumptionTypesWithData(location, result?.byMeter).length > 0;
}

/** Standalone water submeters have no main meter; present them as one measured subtotal. */
export function groupStandaloneWaterTrees(trees: MeterTreeNode[], connections: Connection[],
                                         locationId: string, name: string): MeterTreeNode[] {
    const standaloneIds = new Set(connections
        .filter(connection => connection.info?.connectionType === ConnectionType.Water
            && !(connection.meters || []).some(meter => effectiveMeterType(meter) === MeterType.PRIMARY))
        .map(connection => connection.connectionId));
    const standaloneTrees = trees.filter(tree => standaloneIds.has(tree.connectionId));
    if (standaloneTrees.length < 2) return trees;
    const children = standaloneTrees.flatMap(tree => tree.children)
        .filter(node => meterTreeHasData(node.value));
    const overview: MeterTreeNode = {
        id: `water-submeters-${locationId}`,
        connectionId: `water-submeters-${locationId}`,
        sourceConnectionIds: standaloneTrees.map(tree => tree.connectionId),
        name,
        value: children.filter(node => !node.overlay).reduce((sum, node) => sum + node.value, 0),
        measurementType: DataType.waterIntermediateConsumption,
        children
    };
    return [overview, ...trees.filter(tree => !standaloneIds.has(tree.connectionId))];
}

export function meterTreeMeasurementType(entity: Entity, selectedDataType: DataType): DataType {
    if (!isChargePointMeter(entity)) return selectedDataType;
    return effectiveMeterType(entity.meter) === MeterType.PRIMARY
        ? DataType.chargePointConsumption : DataType.chargePointIntermediateConsumption;
}

export function meterTreeRootTotal(connectionValue: number | null | undefined,
                                   topLevelValues: number[], fallbackValues: number[]): number {
    if (connectionValue != null && connectionValue > 0) return connectionValue;
    const values = topLevelValues.length > 0 ? topLevelValues : fallbackValues;
    return lodash.sum(values);
}

export function calculatedResidualValue(mainMeterValue: number, visibleBranchValues: number[]): number {
    return Math.max((mainMeterValue || 0) - lodash.sum(visibleBranchValues || []), 0);
}

export function sumAlignedMeasurementSeries(series: number[][]): number[] {
    const length = Math.max(0, ...series.map(values => values.length));
    return lodash.range(length).map(index => lodash.sum(series.map(values => values[index] || 0)));
}

export function calculatedResidualSeriesValue(mainMeterValues: number[], visibleBranchSeries: number[][]): number {
    const allocatedValues = sumAlignedMeasurementSeries(visibleBranchSeries);
    return lodash.sum(mainMeterValues.map((mainValue, index) =>
        calculatedResidualValue(mainValue, [allocatedValues[index] || 0])));
}

export function meterTreeBranchOrder(entity?: Entity): number {
    if (isChargePointMeter(entity)) return 1;
    if (isCalculatedResidual(entity?.meter)) return 2;
    if (entity?.meter?.info?.virtual) return 3;
    return 0;
}

export interface MeterTreeEntityIndex {
    entitiesByMeterId: Map<string, Entity>;
    entitiesByTreeConnectionId: Map<string, Entity[]>;
    childIdsByMeterId: Map<string, string[]>;
    locationConnectionsById: Map<string, Connection>;
    meterConnectionsById: Map<string, Connection>;
    residualEntitiesByConnectionId: Map<string, Entity>;
    ownConnectionIds: string[];
}

export function buildMeterTreeEntityIndex(entities: Entity[], locationConnections: Connection[] = []): MeterTreeEntityIndex {
    const entitiesByMeterId = new Map<string, Entity>();
    const entitiesByTreeConnectionId = new Map<string, Entity[]>();
    const locationConnectionsById = new Map<string, Connection>();
    const meterConnectionsById = new Map<string, Connection>();
    const residualEntitiesByConnectionId = new Map<string, Entity>();
    const ownConnectionIds: string[] = [];
    const seenOwnConnectionIds = new Set<string>();

    locationConnections.forEach(connection => {
        if (connection?.connectionId && !locationConnectionsById.has(connection.connectionId)) {
            locationConnectionsById.set(connection.connectionId, connection);
        }
    });

    const displayHierarchyByConnectionId = new Map<string, ReturnType<typeof buildMeterDisplayHierarchy>>();
    const displayHierarchy = (connection: Connection) => {
        if (!displayHierarchyByConnectionId.has(connection.connectionId)) {
            displayHierarchyByConnectionId.set(connection.connectionId,
                buildMeterDisplayHierarchy(connection.meters || []));
        }
        return displayHierarchyByConnectionId.get(connection.connectionId)!;
    };

    entities.forEach(entity => {
        const meterId = entity.meter?.meterId;
        const connectionId = entity.connection?.connectionId;
        if (!meterId) return;
        if (!connectionId) return;
        if (!displayHierarchy(entity.connection).visibleIds.has(entity.meter.meterId)) return;

        entitiesByMeterId.set(meterId, entity);

        if (!meterConnectionsById.has(connectionId)) meterConnectionsById.set(connectionId, entity.connection);
        if (!seenOwnConnectionIds.has(connectionId)) {
            seenOwnConnectionIds.add(connectionId);
            ownConnectionIds.push(connectionId);
        }

        const treeConnectionIds = new Set([connectionId,
            linkedElectricityConnectionId(entity.connection, locationConnections)]);
        treeConnectionIds.forEach(treeConnectionId => {
            if (!treeConnectionId) return;
            const connectionEntities = entitiesByTreeConnectionId.get(treeConnectionId) || [];
            connectionEntities.push(entity);
            entitiesByTreeConnectionId.set(treeConnectionId, connectionEntities);
        });

        if (isCalculatedResidual(entity.meter) && !residualEntitiesByConnectionId.has(connectionId)) {
            residualEntitiesByConnectionId.set(connectionId, entity);
        }
    });

    const childIdsByMeterId = new Map<string, string[]>();
    meterConnectionsById.forEach(connection => {
        const displayHierarchy = buildMeterDisplayHierarchy(connection.meters || []);
        displayHierarchy.childIds.forEach((childIds, parentId) => {
            childIdsByMeterId.set(parentId, childIds);
        });
    });

    return {
        entitiesByMeterId,
        entitiesByTreeConnectionId,
        childIdsByMeterId,
        locationConnectionsById,
        meterConnectionsById,
        residualEntitiesByConnectionId,
        ownConnectionIds
    };
}

function hklNumber(values: (string | null | undefined)[]): string | null {
    for (const value of values) {
        const match = value?.match(/\bHKL[\s-]*([1-5])\b/i);
        if (match) return match[1];
    }
    return null;
}

export function groupChargePointMeterRecords(records: PerformanceRecord[], entities: Entity[],
                                              connectionId: string, label: string): PerformanceRecord[] {
    const entitiesByMeterId = new Map(entities
        .filter(entity => !!entity.meter?.meterId)
        .map(entity => [entity.meter.meterId, entity]));
    const chargePointRecords = records.filter(record => isChargePointMeter(entitiesByMeterId.get(record.entityId)));
    if (chargePointRecords.length < 2) return records;

    const sourceTotals = chargePointRecords.filter(record => {
        const entity = entitiesByMeterId.get(record.entityId);
        return entity?.connection?.info.connectionType === ConnectionType.ChargePoint
            && effectiveMeterType(entity.meter) === MeterType.PRIMARY;
    });
    const detailRecords = chargePointRecords.filter(record => !sourceTotals.includes(record));
    const manuallyClassifiedRecords = detailRecords.filter(record => {
        const entity = entitiesByMeterId.get(record.entityId);
        return entity?.connection?.info.connectionType !== ConnectionType.ChargePoint
            && (entity?.meter?.details as any)?.chargePoint === true;
    });
    const groupId = `charge-point-group-${connectionId}`;
    const children = detailRecords.map(record => ({...record, parentId: groupId}));
    const totalRecords = sourceTotals.length > 0
        ? [...sourceTotals, ...manuallyClassifiedRecords]
        : detailRecords;
    const group: PerformanceRecord = {
        entityId: groupId,
        value: lodash.sum(totalRecords.map(record => record.value || 0)),
        estimatedValue: null,
        label,
        parentId: connectionId,
        childIds: children.map(record => record.entityId),
        virtual: true,
        type: MeterType.INTERMEDIATE,
        measurementType: DataType.chargePointConsumption
    };
    const chargePointIds = new Set(chargePointRecords.map(record => record.entityId));
    return [...records.filter(record => !chargePointIds.has(record.entityId)), group, ...children];
}

export function meterTreeQueryResolution(timeRange: DashboardTime): TimeResolution {
    return timeRange.resolution || TimeResolution.month;
}

export function meterTreeDataRequest(
    location: {locationId?: string; aliasIds?: string[]} | null | undefined,
    timeRange: DashboardTime | null | undefined
): GetMeterTreeData | null {
    const locationIds = [...new Set([location?.locationId, ...(location?.aliasIds ?? [])]
        .filter((locationId): locationId is string =>
            typeof locationId === 'string' && locationId.trim().length > 0))];
    if (!locationIds.length || !timeRange?.start || !timeRange?.end) return null;
    return {
        resolution: meterTreeQueryResolution(timeRange),
        timeRange: AppContext.timeRangeToQuery(timeRange),
        locationIds,
        unrounded: true
    };
}

function singleFilterValue(category: string, value: string | null | undefined): MeterFilterEntry[] {
    const normalised = value?.trim();
    return normalised ? [{category, value: normalised}] : [];
}

function isStandardFilterTag(tag): boolean {
    return (['tenant', 'floor', 'tower', 'wing'] as MeterMetadataField[])
        .some(field => RefdataUtils.isMeterMetadataTag(tag, field))
        || STANDARD_FILTER_CATEGORIES.has(tag.category === 'custom' ? tag.customType : tag.category);
}

@Component({
    selector: 'app-meter-tree-view',
    templateUrl: './meter-tree-view.component.html',
    styleUrl: './meter-tree-view.component.css'
})
export class MeterTreeViewComponent extends EntityPerformanceChartComponent implements OnInit {
    chartInstances = new Map<string, any>();
    private deckControllers = new Map<string, ReturnType<typeof createMeterTreeDeckController>>();
    chartOptions = new Map<string, EChartsOption>();
    treeData = new Map<string, any>();
    selectedTreeKey: string | null = null;
    selectedDataType: DataType = DataType.electricityIntermediateConsumption;
    perSquareMeterEnabled = true;
    sourceProvider: SourcesProvider<any> = new OrganisationProvider();
    meters: Entity[] = [];
    private availableMeters: Entity[] = [];
    private meterIndex = buildMeterTreeEntityIndex([]);
    @Input() selectedLocation: Entity | null = null;
    @Output() meterTreeDataAvailable = new EventEmitter<boolean>();
    @Output() measurementsReceived = new EventEmitter<MeterTreeMeasurementsResult>();
    tagFilters: Record<string, string[]> = {};
    private latestMeasurementsResult: MeterTreeMeasurementsResult;
    private groupedValuesByEntity = new Map<string, number[]>();
    private readonly meterTreeDataRequests = new Subject<GetMeterTreeData | null>();
    meterNameOptions = Object.values(MeterTreeName);
    meterNameFormatter = (value: MeterTreeName): string => TranslateDirective.getTranslation(value, true) as string;
    meterNameSelectable = (value: MeterTreeName): boolean => meterTreeNameSelectable(value, this.availableMeters);
    @ViewChild('meterTreeTable') tableElement: ElementRef;

    constructor(private readonly meterTreeNamePreference: MeterTreeNamePreferenceService,
                private readonly destroyRef: DestroyRef) {
        super();
    }

    ngOnInit() {
        super.ngOnInit();
        this.options.meterTreeView.selectedMeterName =
            this.meterTreeNamePreference.selectedName(AppContext.userProfile?.userId);
        this.meterTreeNamePreference.selectedName$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(selectedName => {
                if (!selectedName || !this.options?.meterTreeView
                    || this.options.meterTreeView.selectedMeterName === selectedName) return;
                this.options.meterTreeView.selectedMeterName = selectedName;
                this.updateMeterTree();
            });
        this.meterTreeDataRequests.pipe(
            switchMap(request => request
                ? this.getData(request).pipe(catchError(() => EMPTY))
                : EMPTY),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(result => {
            this.latestMeasurementsResult = result;
            this.applyMeasurementsResult(result);
        });
        this.selectDefaultIntermediateConsumptionType();
        this.options.selectedDataType = this.getSelectedDataType;
        this.sendQuery("getEntityConsumptionType").subscribe(e => {
            if (e && this.possibleDataTypes().includes(e)) {
                this.options.selectedDataType = this.selectedDataType = e;
            }
        });
        this.subscribeTo("getChartDataQueryFilters").subscribe((f: DataQueryFilters) => {
            this.filters = f;
            this.refresh();
        });
        if (this.selectedLocation?.location?.locationId) {
            this.subscribeTo("getEntity", this.selectedLocation.location.locationId)
                .pipe(skip(1), debounceTime(100))
                .subscribe((location: Entity) => {
                    if (!location) return;
                    this.selectedLocation = location;
                    this.selectDefaultIntermediateConsumptionType();
                    if (this.filters?.timeRange) {
                        this.refresh();
                    } else {
                        this.refreshMeters();
                    }
                    if (!this.filters?.timeRange && this.latestMeasurementsResult) {
                        this.applyMeasurementsResult(this.latestMeasurementsResult);
                    }
                });
        }
    }

    private refreshMeters() {
        if (!this.selectedLocation?.location) {
            this.meters = [];
            this.availableMeters = [];
            this.meterIndex = buildMeterTreeEntityIndex([]);
            this.reconcileMeterNameSelection();
            return;
        }
        const meterEntities = this.selectedLocation.location.connections.flatMap(c => {
            const displayHierarchy = buildMeterDisplayHierarchy(c.meters || []);
            const entities = c.meters.filter(m => displayHierarchy.visibleIds.has(m.meterId))
                .map(m => new Entity(this.selectedLocation.organisation,
                this.selectedLocation.location, c, m));
            if (c.info.connectionType === ConnectionType.Electricity
                && !c.meters.some(isCalculatedResidual)) {
                const residual = calculatedResidualEntity(this.selectedLocation.organisation,
                    this.selectedLocation.location, c);
                if (residual) entities.push(residual);
            }
            return entities;
        });
        const availableMeters = this.filterMetersBasedOnSelectedDataType(meterEntities)
            .filter(entity => isMeterVisible(entity.meter));
        this.availableMeters = availableMeters;
        this.extractTagFilters(availableMeters);
        this.meters = this.applyTagFilters(availableMeters);
        this.meterIndex = buildMeterTreeEntityIndex(this.meters, this.selectedLocation.location.connections);
        this.reconcileMeterNameSelection();
    }

    private reconcileMeterNameSelection() {
        const selectedName = this.options.meterTreeView.selectedMeterName || MeterTreeName.Name;
        if (!meterTreeNameSelectable(selectedName, this.availableMeters)) {
            this.options.meterTreeView.selectedMeterName = MeterTreeName.Name;
            this.meterTreeNamePreference.selectName(MeterTreeName.Name, AppContext.userProfile?.userId);
        }
    }

    private extractTagFilters(availableMeters: Entity[]) {
        const previousSelections = {...this.options.meterTreeView.selectedTagFilters};
        this.tagFilters = {};
        availableMeters.forEach(m => {
            meterFilterEntries(m).forEach(entry => {
                this.tagFilters[entry.category] ??= [];
                if (!this.tagFilters[entry.category].includes(entry.value)) {
                    this.tagFilters[entry.category].push(entry.value);
                }
            });
        });
        Object.values(this.tagFilters).forEach(values => values.sort((a, b) => a.localeCompare(b)));
        this.options.meterTreeView.selectedTagFilters = reconcileSelectedMeterFilters(previousSelections, this.tagFilters);
    }

    private applyTagFilters(meters: Entity[]): Entity[] {
        return meters.filter(entity => meterMatchesFilters(entity, this.options.meterTreeView.selectedTagFilters));
    }

    refresh = () => {
        this.refreshMeters();
        this.meterTreeDataRequests.next(meterTreeDataRequest(
            this.selectedLocation?.location,
            this.filters?.timeRange
        ));
    };

    private applyMeasurementsResult(result: MeterTreeMeasurementsResult) {
        const availableTypes = intermediateConsumptionTypesWithData(this.selectedLocation?.location, result.byMeter);
        this.selectedDataType = intermediateConsumptionTypeForPeriod(availableTypes, this.selectedDataType);
        this.options.selectedDataType = this.selectedDataType;
        const chartData: ChartDataPerMeasurement = {
            totals: result.byConnection,
            byEntity: result.byMeter.filter(v => this.meterIndex.entitiesByMeterId.has(v.entityId))
        };
        this.setData(chartData);
        this.showChart = !!chartData.byEntity?.length;
        this.meterTreeDataAvailable.emit(availableTypes.length > 0);
        this.measurementsReceived.emit(result);
    }

    override setData(result: ChartDataPerMeasurement) {
        super.setData(result);
        this.updateMeterTree();
    }

    hasIntermediaryMeters = () => hasEligibleMeterTreeMeters(this.selectedLocation?.location);

    createData = (result: ChartDataPerMeasurement): Observable<PerformanceRecord[]> => {
        if (!result) return of([]);
        this.groupedValuesByEntity.clear();
        const displayHierarchyByConnectionId = new Map<string, ReturnType<typeof buildMeterDisplayHierarchy>>();
        const displayHierarchy = (connection: Connection) => {
            if (!displayHierarchyByConnectionId.has(connection.connectionId)) {
                displayHierarchyByConnectionId.set(connection.connectionId,
                buildMeterDisplayHierarchy(connection.meters || []));
            }
            return displayHierarchyByConnectionId.get(connection.connectionId)!;
        };
        const meterRecords = (result.byEntity ?? []).map(m => {
            const entity = this.meterIndex.entitiesByMeterId.get(m.entityId);
            if (!entity || !entity.meter) return null;
            const measurementType = meterTreeMeasurementType(entity, this.selectedDataType);
            const sourceMeasurementType = entity.connection?.info.connectionType === ConnectionType.ChargePoint
                ? measurementType : this.selectedDataType;
            const measurements = new Measurements(m, sourceMeasurementType, this.filters.timeRange);
            const area = RefdataUtils.getMeterArea(entity.meter);
            const groupedValues = measurements.groupMeasurements();
            this.groupedValuesByEntity.set(entity.meter.meterId, groupedValues);
            const totalValue = lodash.sum(groupedValues);
            const value = this.showPerSquareMeter() ? (area ? lodash.round(totalValue / area, 1) : null) : totalValue;
            return <PerformanceRecord>{
                entityId: entity.meter.meterId,
                value,
                estimatedValue: null,
                label: RefdataUtils.meterFormatter(entity),
                parentId: displayHierarchy(entity.connection).parentIds.get(entity.meter.meterId)
                    || entity.connection.connectionId,
                childIds: displayHierarchy(entity.connection).childIds.get(entity.meter.meterId) || [],
                virtual: entity.meter.info.virtual,
                type: entity.meter.info.type || entity.meter.details?.type,
                measurementType
            };
        }).filter((r): r is PerformanceRecord => !!r);
        const connectionRecords = (result.totals ?? []).map(r => {
            const connection = this.meterIndex.locationConnectionsById.get(r.entityId);
            if (!connection) return null;
            const measurementType = connection.info.connectionType === ConnectionType.ChargePoint
                ? DataType.chargePointConsumption
                : this.intermediateMeasurementLink().get(this.getSelectedDataType);
            const measurements = new Measurements(r, measurementType, this.filters.timeRange);
            const groupedValues = measurements.groupMeasurements();
            this.groupedValuesByEntity.set(connection.connectionId, groupedValues);
            const totalValue = lodash.sum(groupedValues);
            return <PerformanceRecord>{
                entityId: connection.connectionId,
                value: totalValue,
                estimatedValue: null,
                label: RefdataUtils.connectionFormatter(connection),
                parentId: null,
                childIds: connection.meters?.map(m => m.meterId) || [],
                type: null,
                measurementType
            };
        }).filter((r): r is PerformanceRecord => !!r);
        return of([...meterRecords, ...connectionRecords]);
    };

    updateMeterTree() {
        const data = this.prepareTreeData();
        const previousSelectedKey = this.selectedTreeKey;
        this.treeData.clear();
        this.chartOptions.clear();
        for (const tree of data) {
            // ECharts only applies initialTreeDepth when it owns expansion clicks.
            // Our click handler owns that state, so explicitly keep the original
            // default: root plus one child lane, with deeper branches closed.
            initializeMeterTreeExpansion(tree);
            this.treeData.set(tree.connectionId, tree);
            this.chartOptions.set(tree.connectionId, this.createOptionForTree(tree));
        }
        this.selectedTreeKey = previousSelectedKey && this.treeData.has(previousSelectedKey)
            ? previousSelectedKey
            : data[0]?.connectionId || null;
    }

    private findNearestAncestor(startParentId: string | undefined, connId: string,
                                included: Set<string>, meterData: Map<string, PerformanceRecord>): string | null {
        let p = startParentId;
        while (p) {
            if (p === connId) return null;
            if (included.has(p)) return p;
            const parentRec = meterData.get(p);
            if (!parentRec) break;
            p = parentRec.parentId;
        }
        return null;
    }

    private buildNode(rec: PerformanceRecord, included: Set<string>, meterData: Map<string, PerformanceRecord>,
                      meterIdsWithData: Set<string>, overlayIds: Set<string>,
                      meterEntitiesById: Map<string, Entity>): MeterTreeNode {
        const children = (rec.childIds || []).filter(id => included.has(id))
            .map(id => meterData.get(id)).filter(Boolean)
            .map(child => this.buildNode(
                child!, included, meterData, meterIdsWithData, overlayIds, meterEntitiesById));
        const aggregate = !meterIdsWithData.has(rec.entityId) && children.length > 0;
        const entity = meterEntitiesById.get(rec.entityId);
        const calculatedResidual = rec.entityId?.startsWith(CALCULATED_RESIDUAL_METER_PREFIX);
        return {
            id: rec.entityId,
            entityId: rec.entityId,
            name: rec.label,
            value: aggregate ? lodash.sum(children.map(child => child.value || 0)) : Math.max(rec.value || 0, 0),
            children,
            measurementType: rec.measurementType,
            calculatedResidual,
            historical: entity?.meter?.active === false,
            overlay: overlayIds.has(rec.entityId),
            aggregate
        };
    }

    private updateNodeVisuals(node: MeterTreeNode, rootValue: number, parentActive = false,
                              siblingIndex = 0, depth = 0) {
        const ratio = rootValue > 0 ? (node.flowValue ?? node.value ?? 0) / rootValue : 0;
        const measurementColor = DashboardContext.getMeasurementColor('current', node.measurementType || this.getSelectedDataType, 0);
        const nodeColor = node.overlay ? OVERLAY_COLOR
            : node.calculatedResidual ? RESIDUAL_COLOR
                : node.historical ? HISTORICAL_COLOR : measurementColor || FLOW_COLOR;
        node.lineStyle = {
            width: node.connectionId ? 0 : Math.max(MIN_FLOW_LINE_WIDTH, ratio * FLOW_LINE_WIDTH),
            color: parentActive ? FLOW_COLOR : nodeColor,
            opacity: parentActive ? 0.72 : node.overlay ? 0.46 : node.historical ? 0.32 : 0.44,
            type: 'solid',
            curveness: 0.56,
            cap: 'round'
        };
        const active = !!node.active;
        const selected = meterTreeCardSelected(node);
        node.cardBorderColor = selected ? ACTIVE_CARD_BORDER : node.connectionId ? '#cad6e6' : '#dce3ec';
        node.cardBorderWidth = selected ? 1.5 : 1;
        node.itemStyle = {
            color: meterTreeCardFill(node, selected, siblingIndex, depth),
            borderColor: 'transparent',
            borderWidth: 0,
            borderType: 'solid',
            borderRadius: 16,
            shadowBlur: selected ? 0 : 4,
            shadowOffsetY: selected ? 0 : 2,
            shadowColor: selected ? 'transparent' : 'rgba(31, 55, 90, 0.08)'
        };
        node.emphasis = {
            itemStyle: {...node.itemStyle}
        };
        (node.children || []).forEach((child, index) =>
            this.updateNodeVisuals(child, rootValue, active, index, depth + 1));
    }

    prepareTreeData() {
        if (!this.records?.length || !this.meters?.length) return [];
        const connectionData = new Map<string, PerformanceRecord>();
        const meterData = new Map<string, PerformanceRecord>();
        this.records.forEach(r =>
            (r.type == null ? connectionData : meterData).set(r.entityId, r)
        );
        const displayHierarchyByConnectionId = new Map<string, ReturnType<typeof buildMeterDisplayHierarchy>>();
        const displayHierarchy = (connection: Connection) => {
            if (!displayHierarchyByConnectionId.has(connection.connectionId)) {
                displayHierarchyByConnectionId.set(connection.connectionId,
                buildMeterDisplayHierarchy(connection.meters || []));
            }
            return displayHierarchyByConnectionId.get(connection.connectionId)!;
        };
        const meterIdsWithData = new Set(meterData.keys());
        const meterEntitiesById = this.meterIndex.entitiesByMeterId;
        this.meters.forEach(e => {
            const meterId = e.meter?.meterId;
            if (!meterId) return;
            const childIds = this.meterIndex.childIdsByMeterId.get(meterId) || [];
            const existingRecord = meterData.get(meterId);
            if (existingRecord) {
                existingRecord.label = meterTreeName(e, this.options.meterTreeView.selectedMeterName);
                return;
            }
            if (childIds.length === 0) return;
            meterData.set(meterId, {
                entityId: meterId,
                value: 0,
                estimatedValue: null,
                label: meterTreeName(e, this.options.meterTreeView.selectedMeterName),
                parentId: displayHierarchy(e.connection).parentIds.get(e.meter.meterId) || e.connection.connectionId,
                childIds: childIds,
                virtual: e.meter.info.virtual,
                type: e.meter.info.type || e.meter.details?.type,
                measurementType: meterTreeMeasurementType(e, this.selectedDataType)
            });
        });
        const connectionIds = this.getSelectedDataType === DataType.electricityIntermediateConsumption
            ? this.selectedLocation.location.connections
                .filter(connection => connection.info.connectionType === ConnectionType.Electricity)
                .map(connection => connection.connectionId)
            : this.meterIndex.ownConnectionIds;
        const trees = connectionIds.map(connId => {
            const connRecord = connectionData.get(connId);
            let meterRecords = (this.meterIndex.entitiesByTreeConnectionId.get(connId) || [])
                .map(e => meterData.get(e.meter?.meterId!))
                .filter((r): r is PerformanceRecord => !!r);
            const chargePointRecordWithData = meterRecords.some(record => meterIdsWithData.has(record.entityId)
                && isChargePointMeter(this.meters.find(entity => entity.meter?.meterId === record.entityId)));
            const chargePointGroupId = `charge-point-group-${connId}`;
            const chargePointSourceSeries = this.chargePointSourceSeries(meterRecords);
            meterRecords = groupChargePointMeterRecords(meterRecords, this.meters, connId,
                TranslateDirective.getTranslation('Charge points total', true) as string);
            if (chargePointSourceSeries.length) {
                this.groupedValuesByEntity.set(chargePointGroupId, chargePointSourceSeries);
            }
            meterRecords.forEach(record => meterData.set(record.entityId, record));
            const chargePointGroup = meterRecords.find(record => record.entityId === `charge-point-group-${connId}`);
            if (chargePointGroup && chargePointRecordWithData) meterIdsWithData.add(chargePointGroup.entityId);
            let included = new Set(meterRecords.map(r => r.entityId));
            let topMeters = meterRecords.filter(r =>
                this.findNearestAncestor(r.parentId, connId, included, meterData) === null);
            const overlayIds = meterTreeOverlayIds(topMeters);
            const topMetersWithData = topMeters.filter(r => meterIdsWithData.has(r.entityId));
            const includedWithData = new Set(meterRecords
                .filter(r => meterIdsWithData.has(r.entityId))
                .map(r => r.entityId));
            const firstLevelMetersWithData = meterRecords.filter(r => meterIdsWithData.has(r.entityId))
                .filter(r => this.findNearestAncestor(r.parentId, connId, includedWithData, meterData) === null);
            const total = meterTreeRootTotal(
                connRecord?.value,
                topMetersWithData.filter(record => !overlayIds.has(record.entityId)).map(r => r.value || 0),
                firstLevelMetersWithData.filter(record => !overlayIds.has(record.entityId)).map(r => r.value || 0)
            );
            const residualEntity = this.meterIndex.residualEntitiesByConnectionId.get(connId);
            if (residualEntity) {
                const physicalRecords = physicalMeterTreeRecords(topMeters);
                const allocated = physicalRecords.reduce((sum, record) => sum + (record.value || 0), 0);
                const mainSeries = this.groupedValuesByEntity.get(connId) || [];
                const allocatedSeries = physicalRecords
                    .map(record => this.groupedValuesByEntity.get(record.entityId) || [])
                    .filter(values => values.length > 0);
                const residualValue = mainSeries.length > 0
                    ? calculatedResidualSeriesValue(mainSeries, allocatedSeries)
                    : calculatedResidualValue(total, [allocated]);
                const residualRecord: PerformanceRecord = {
                    entityId: residualEntity.meter.meterId,
                    value: residualValue,
                    estimatedValue: null,
                    label: meterTreeName(residualEntity, this.options.meterTreeView.selectedMeterName),
                    parentId: connId,
                    childIds: [],
                    virtual: true,
                    type: MeterType.INTERMEDIATE,
                    measurementType: DataType.electricityIntermediateConsumption
                };
                meterData.set(residualRecord.entityId, residualRecord);
                meterRecords = [...meterRecords.filter(record => record.entityId !== residualRecord.entityId), residualRecord];
                included = new Set(meterRecords.map(record => record.entityId));
                topMeters = meterRecords.filter(record =>
                    this.findNearestAncestor(record.parentId, connId, included, meterData) === null);
            }
            const childrenRoots = [...topMeters].sort((left, right) => {
                const leftEntity = meterEntitiesById.get(left.entityId);
                const rightEntity = meterEntitiesById.get(right.entityId);
                const leftOrder = left.measurementType === DataType.chargePointConsumption
                    ? 1 : meterTreeBranchOrder(leftEntity);
                const rightOrder = right.measurementType === DataType.chargePointConsumption
                    ? 1 : meterTreeBranchOrder(rightEntity);
                if (leftOrder !== rightOrder) return leftOrder - rightOrder;
                const leftHkl = hklNumber([left.label]);
                const rightHkl = hklNumber([right.label]);
                if (leftHkl && rightHkl && leftHkl !== rightHkl) return Number(leftHkl) - Number(rightHkl);
                if (leftHkl && leftHkl === rightHkl) {
                    return Number(leftEntity?.meter?.active !== false) - Number(rightEntity?.meter?.active !== false);
                }
                return 0;
            });
            const children = childrenRoots.map(mr =>
                this.buildNode(mr, included, meterData, meterIdsWithData, overlayIds, meterEntitiesById)).filter(Boolean);
            const connection = this.meterIndex.meterConnectionsById.get(connId);
            const name = connection ? connectionTreeName(connection) : connRecord?.label || connId;
            const tree: MeterTreeNode = {
                id: connId,
                name,
                connectionId: connId,
                value: total,
                measurementType: connRecord?.measurementType,
                children
            };
            return tree;
        }).filter(tree => meterTreeHasData(tree.value));
        const displayedTrees = this.getSelectedDataType === DataType.waterIntermediateConsumption
            ? groupStandaloneWaterTrees(trees, this.selectedLocation.location.connections,
                this.selectedLocation.location.locationId,
                TranslateDirective.getTranslation('Submeter total', true) as string)
            : trees;
        displayedTrees.forEach(tree => {
            allocateMeterTreeFlows(tree);
            this.updateNodeVisuals(tree, tree.value);
        });
        return displayedTrees;
    }

    private chargePointSourceSeries(records: PerformanceRecord[]): number[] {
        const entitiesByMeterId = new Map(this.meters
            .filter(entity => !!entity.meter?.meterId)
            .map(entity => [entity.meter.meterId, entity]));
        const chargePointRecords = records.filter(record => isChargePointMeter(entitiesByMeterId.get(record.entityId)));
        if (chargePointRecords.length < 2) return [];
        const sourceTotals = chargePointRecords.filter(record => {
            const entity = entitiesByMeterId.get(record.entityId);
            return entity?.connection?.info.connectionType === ConnectionType.ChargePoint
                && effectiveMeterType(entity.meter) === MeterType.PRIMARY;
        });
        const detailRecords = chargePointRecords.filter(record => !sourceTotals.includes(record));
        const manuallyClassifiedRecords = detailRecords.filter(record => {
            const entity = entitiesByMeterId.get(record.entityId);
            return entity?.connection?.info.connectionType !== ConnectionType.ChargePoint
                && (entity?.meter?.details as any)?.chargePoint === true;
        });
        const totalRecords = sourceTotals.length > 0
            ? [...sourceTotals, ...manuallyClassifiedRecords]
            : detailRecords;
        return sumAlignedMeasurementSeries(totalRecords
            .map(record => this.groupedValuesByEntity.get(record.entityId) || [])
            .filter(values => values.length > 0));
    }

    private getData(request: GetMeterTreeData): Observable<MeterTreeMeasurementsResult> {
        return sendQuery("com.flowmaps.api.measurements.GetMeterTreeData", request);
    }

    filterMetersBasedOnSelectedDataType = (selection: Entity[]) => {
        const locationConnections = this.selectedLocation?.location?.connections || [];
        switch (this.getSelectedDataType) {
            case DataType.electricityIntermediateConsumption:
                return selection.filter(entity => {
                    const meterType = effectiveMeterType(entity.meter);
                    return (entity.connection?.info.connectionType === ConnectionType.Electricity
                            && meterType === MeterType.INTERMEDIATE
                            && !isChargePointMeter(entity))
                        || (entity.connection?.info.connectionType === ConnectionType.ChargePoint
                            && !!linkedElectricityConnectionId(entity.connection, locationConnections)
                            && [MeterType.PRIMARY, MeterType.INTERMEDIATE].includes(meterType));
                });
            case DataType.waterIntermediateConsumption:
                return selection.filter(e => (!e.meter || (e.meter.details?.type || e.meter.info.type) === "INTERMEDIATE") && e.connection?.info.connectionType === "Water");
            case DataType.chargePointIntermediateConsumption:
                return selection.filter(e => (!e.meter || (e.meter.details?.type || e.meter.info.type) === "INTERMEDIATE") && e.connection?.info.connectionType === "ChargePoint");
            default:
                return selection;
        }
    }

    closeModal = () => this.sendCommandAndForget("closeModal");

    openModal = () => {
        this.sendCommandAndForget("openChartInModal", <MeasurementsHandlerOpenModalCommand>{
            component: MeterTreeViewComponent,
            chartOptions: {
                [ChartOptionType.Performance]: this.options
            },
            chartModalData: this.modalOptions,
            inputs: {
                selectedLocation: this.selectedLocation
            },
            container: this.container
        });
    }

    get getSelectedDataType() {
        const selectedDataType = super.getSelectedDataType;
        return selectedDataType && this.possibleDataTypes().includes(selectedDataType) ? selectedDataType : this.selectedDataType;
    }

    intermediateMeasurementLink = (): Map<DataType, DataType> => {
        return new Map<DataType, DataType>()
            .set(DataType.electricityIntermediateConsumption, DataType.electricityConsumption)
            .set(DataType.waterIntermediateConsumption, DataType.waterConsumption);
    };

    entityType = (): EntityType => EntityType.meter

    possibleDataTypes = (): DataType[] => [DataType.electricityIntermediateConsumption,
        DataType.waterIntermediateConsumption]

    measurementTypeChanged = (m: DataType) => {
        this.selectedDataType = m;
        this.options.selectedDataType = m;
        this.refresh();
    }

    private selectDefaultIntermediateConsumptionType(): void {
        const defaultType = defaultIntermediateConsumptionType(this.selectedLocation?.location);
        if (this.selectedDataType !== defaultType) {
            this.selectedDataType = defaultType;
            if (this.options) this.options.selectedDataType = this.selectedDataType;
        }
    }

    meterNameChanged = (selectedName: MeterTreeName) => {
        if (!meterTreeNameSelectable(selectedName, this.availableMeters)) return;
        this.options.meterTreeView.selectedMeterName = selectedName;
        this.meterTreeNamePreference.selectName(selectedName, AppContext.userProfile?.userId);
        this.updateMeterTree();
    }

    toggleShowAsTable = () => this.options.meterTreeView.showAsTable = !this.options.meterTreeView.showAsTable;

    get selectedTreeTableRows(): MeterTreeTableRow[] {
        return meterTreeTableRows(this.treeData.get(this.selectedTreeKey));
    }

    tableValue = (row: MeterTreeTableRow): string => this.chartUtils.defaultTooltipFormatter(
        row.value,
        row.measurementType || (row.level === 0
            ? this.intermediateMeasurementLink().get(this.getSelectedDataType) : this.getSelectedDataType)
    );

    trackTableRow = (_index: number, row: MeterTreeTableRow) => row.key;

    createOptionForTree(tree: MeterTreeNode): EChartsOption {
        markMeterTreeCardDetails(tree);
        const labelLayout = {cardWidth: TREE_CARD_MAX_WIDTH};
        const shareLabel = TranslateDirective.getTranslation(tree.sourceConnectionIds
            ? 'Share of total' : 'Share of main meter', true) as string;
        const parentShareLabel = TranslateDirective.getTranslation('Share of parent', true) as string;
        const virtualLabel = TranslateDirective.getTranslation('Virtual breakdown', true) as string;
        const virtualExplanation = TranslateDirective.getTranslation(
            'This virtual breakdown overlaps the physical meter flow and is not added to the total.', true) as string;
        const overAllocationExplanation = TranslateDirective.getTranslation(
            'The meters in this split exceed their parent. Flow shares are scaled to prevent double counting.', true) as string;
        const percentageStyle = (node: MeterTreeNode) => node.overlay ? 'overlayPercent'
            : node.calculatedResidual ? 'residualPercent' : [DataType.chargePointConsumption,
                DataType.chargePointIntermediateConsumption].includes(node.measurementType)
                ? 'chargePointPercent' : node.historical ? 'historicalPercent' : 'percent';
        const nameStyle = (node: MeterTreeNode) => node.connectionId ? 'rootName' : node.overlay ? 'overlayName'
            : node.historical ? 'historicalName' : 'name';
        const branchLabel = (node: MeterTreeNode) => node.children?.length
            ? ` {${meterTreeCardSelected(node) ? 'activeBranch' : 'branch'}|${meterTreeBranchMarker(!!node.selected)}}` : '';
        const warningLabel = (node: MeterTreeNode) => node.overAllocated ? ' {warning|!}' : '';
        const dataTypeForNode = (node: MeterTreeNode) => node.measurementType || (node.connectionId
            ? this.intermediateMeasurementLink().get(this.getSelectedDataType) : this.getSelectedDataType);
        const detailLabel = (node: MeterTreeNode) => this.chartUtils.defaultTooltipFormatter(
            node.value, dataTypeForNode(node));
        const createLabelFormatter = (alwaysShowDetails: boolean) => {
            const formatter: any = (params: any) => {
                const v = params.data as MeterTreeNode;
                const parentPercentage = (v.depth ?? 0) > 1 && v.parentFlowValue != null
                    ? meterTreePercentage(v.flowValue ?? v.value, v.parentFlowValue) : null;
                const secondaryDetails = [
                    parentPercentage ? `↳ ${parentPercentage}` : null,
                    alwaysShowDetails || v.showDetails ? detailLabel(v) : null
                ].filter(Boolean).join(' · ');
                return `{${percentageStyle(v)}|${meterTreePercentage(v.flowValue ?? v.value, tree.value)}}  `
                    + `{${nameStyle(v)}|${fitMeterTreeName(v, labelLayout.cardWidth)}}${branchLabel(v)}${warningLabel(v)}`
                    + (secondaryDetails ? `\n{detailIndent| }  {detail|${secondaryDetails}}` : '');
            };
            formatter.setTreeCardWidth = (cardWidth: number) => labelLayout.cardWidth = cardWidth;
            return formatter;
        };
        const normalLabelFormatter = createLabelFormatter(false);
        const emphasisLabelFormatter = createLabelFormatter(true);
        const labelRich: any = {
            branch: {
                width: 12, align: 'center', fontSize: 15, fontWeight: 650,
                color: '#7b8da7', lineHeight: 21
            },
            activeBranch: {
                width: 12, align: 'center', fontSize: 15, fontWeight: 700,
                color: ACTIVE_CARD_BORDER, lineHeight: 21
            },
            rootName: {fontSize: 12.5, fontWeight: 650, color: '#19355f', lineHeight: 21},
            name: {fontSize: 12, fontWeight: 560, color: '#253858', lineHeight: 21},
            historicalName: {fontSize: 12, fontWeight: 550, color: '#6c7b90', lineHeight: 21},
            overlayName: {fontSize: 12, fontWeight: 620, color: '#6247bb', lineHeight: 21},
            detail: {fontSize: 11, fontWeight: 500, color: '#718097', lineHeight: 16},
            detailIndent: {width: 46, lineHeight: 16},
            percent: {
                width: 42, align: 'right', fontSize: 11.5, fontWeight: 700,
                color: DashboardContext.getMeasurementColor('current', this.getSelectedDataType, 0),
                lineHeight: 21
            },
            historicalPercent: {
                width: 42, align: 'right', fontSize: 11.5, fontWeight: 700,
                color: HISTORICAL_COLOR, lineHeight: 21
            },
            chargePointPercent: {
                width: 42, align: 'right', fontSize: 11.5, fontWeight: 700,
                color: DashboardContext.getMeasurementColor(
                    'current', DataType.chargePointIntermediateConsumption, 0), lineHeight: 21
            },
            residualPercent: {
                width: 42, align: 'right', fontSize: 11.5, fontWeight: 700,
                color: CALCULATED_RESIDUAL_COLOR, lineHeight: 21
            },
            overlayPercent: {
                width: 42, align: 'right', fontSize: 11.5, fontWeight: 700,
                color: OVERLAY_COLOR, lineHeight: 21
            },
            warning: {
                fontSize: 10, fontWeight: 700, color: '#9a4d0b', backgroundColor: '#fff1dc',
                borderRadius: 9, padding: [2, 5]
            }
        };
        return {
            animationDuration: 420,
            animationDurationUpdate: METER_TREE_LANE_ANIMATION_DURATION,
            animationEasing: 'cubicOut',
            animationEasingUpdate: METER_TREE_LANE_ANIMATION_EASING,
            stateAnimation: {duration: 0},
            tooltip: {
                trigger: 'item',
                triggerOn: 'mousemove',
                transitionDuration: 0,
                hideDelay: 0,
                position: (point: number[], _params: any, _dom: any, _rect: any, size: any) => [
                    Math.max(12, point[0] - 560),
                    Math.max(12, Math.min(point[1] + 18, size.viewSize[1] - size.contentSize[1] - 12))
                ],
                borderRadius: 10,
                padding: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                borderColor: '#d8e2f0',
                borderWidth: 1,
                extraCssText: 'box-shadow:0 12px 32px rgba(31,55,90,.16);overflow:hidden;',
                textStyle: {color: '#172b4d', fontSize: 13},
                formatter: (params: any) => {
                    const v = params.data as MeterTreeNode;
                    const flowPercent = meterTreePercentage(v.flowValue ?? v.value, tree.value);
                    const rawPercent = meterTreePercentage(v.value, tree.value);
                    const parentPercent = (v.depth ?? 0) > 1 && v.parentFlowValue != null
                        ? meterTreePercentage(v.flowValue ?? v.value, v.parentFlowValue) : null;
                    const dataType = v.measurementType || (v.connectionId
                        ? this.intermediateMeasurementLink().get(this.getSelectedDataType) : this.getSelectedDataType);
                    const dataTypeName = this.measurementName(dataType, true);
                    const color = v.overlay ? OVERLAY_COLOR : v.calculatedResidual ? CALCULATED_RESIDUAL_COLOR
                        : DashboardContext.getMeasurementColor('current', dataType, 0);
                    const shares = [
                        v.overlay ? `${virtualLabel}: ${rawPercent}` : `${shareLabel}: ${flowPercent}`,
                        parentPercent ? `${parentShareLabel}: ${parentPercent}` : null
                    ].filter(Boolean).map(value =>
                        `<div style="margin-top:4px;padding-left:16px;color:#334e7d;font-weight:600;">${value}</div>`
                    ).join('');
                    const status = v.overlay
                        ? `<div style="margin-top:8px;padding:8px 10px;border-radius:8px;background:#f6f3ff;color:#5f45bd;max-width:320px;white-space:normal;">${virtualExplanation}</div>`
                        : v.overAllocated || v.scaled
                            ? `<div style="margin-top:8px;padding:8px 10px;border-radius:8px;background:#fff7ed;color:#9a4d0b;max-width:320px;white-space:normal;">${overAllocationExplanation}</div>`
                            : '';
                    return `
                    <div class="notranslate" style="padding:12px 14px;">
                        <div style="font-weight:650;color:#172b4d;margin-bottom:8px;max-width:320px;white-space:normal;">${escapeMeterTreeHtml(v.name)}</div>
                        <div style="display:flex;align-items:center;gap:8px;white-space:nowrap;">
                            <span style="width:8px;height:8px;border-radius:999px;background:${color};flex:none;"></span>
                            <span style="color:#5d6b82;">${dataTypeName}: ${this.chartUtils.defaultTooltipFormatter(v.value, dataType)}</span>
                        </div>
                        ${shares}
                        ${status}
                    </div>`;
                }
            },
            series: [{
                type: 'tree',
                data: [tree],
                top: 24,
                bottom: 24,
                symbol: METER_TREE_CARD_SYMBOL,
                symbolSize: [TREE_CARD_MAX_WIDTH, TREE_CARD_HEIGHT],
                initialTreeDepth: 1,
                expandAndCollapse: false,
                layout: 'orthogonal',
                orient: 'LR',
                edgeForkPosition: '52%',
                roam: false,
                lineStyle: {color: '#c7d4e5', width: 1.5, opacity: 0.6, curveness: 0.56},
                itemStyle: {
                    color: 'white',
                    borderColor: 'transparent',
                    borderWidth: 0,
                    borderRadius: 12,
                    shadowBlur: 4,
                    shadowOffsetY: 2,
                    shadowColor: 'rgba(31,55,90,0.08)'
                } as any,
                label: {
                    position: 'insideTopLeft',
                    verticalAlign: 'top',
                    align: 'left',
                    distance: 0,
                    padding: [3, 10, 0, 8],
                    formatter: normalLabelFormatter,
                    rich: labelRich
                },
                leaves: {
                    emphasis: {
                        focus: 'none', scale: false
                    }
                },
                emphasis: {
                    focus: 'none',
                    scale: false,
                    label: {
                        position: 'insideTopLeft',
                        verticalAlign: 'top',
                        align: 'left',
                        distance: 0,
                        padding: [3, 10, 0, 8],
                        formatter: emphasisLabelFormatter,
                        rich: labelRich
                    },
                },
                blur: {
                    label: {opacity: 0.76},
                    itemStyle: {opacity: 0.84},
                    lineStyle: {opacity: 0.42}
                }
            }]
        };
    }


    onChartInit(ec: any, key: string) {
        this.chartInstances.set(key, ec);
        const deckController = createMeterTreeDeckController(ec);
        this.deckControllers.set(key, deckController);
        const keepCardsInDeck = () => keepMeterTreeCardsInDeck(ec);
        ec.on('finished', keepCardsInDeck);
        Promise.resolve().then(keepCardsInDeck);
        ec.on('mouseover', {seriesIndex: 0}, (params: any) => deckController.reveal(params.dataIndex));
        ec.on('mouseout', {seriesIndex: 0}, (params: any) => deckController.conceal(params.dataIndex));
        ec.getZr().on('globalout', deckController.close);
        ec.on('mousemove', {seriesIndex: 0}, (params: any) => {
            const node = params.data;
            if (!node) return;
            if (!node.children || node.children.length === 0) {
                ec.getZr().setCursorStyle('default');
            } else {
                ec.getZr().setCursorStyle('pointer');
            }
        });
    }

    onTagFilterChanged(category: string, selectedValue: string | null) {
        if (selectedValue === null) {
            delete this.options.meterTreeView.selectedTagFilters[category];
        } else {
            this.options.meterTreeView.selectedTagFilters[category] = selectedValue;
        }
        this.refresh();
    }

    handleChartClick(payload: { event: any, key: string | null }) {
        const {event, key} = payload;
        if (!key) return;
        this.onChartEvent(event, key);
    }

    onChartEvent($event: any, key: string) {
        const clickedKey = $event.data?.id || $event.data?.entityId || $event.data?.connectionId || $event.data?.name;
        if (!clickedKey) return;
        const originalTree = this.treeData.get(key);
        if (!originalTree) return;
        const clickedPath = this.findNodePath([originalTree], clickedKey);
        if (!clickedPath) return;
        const clickedNode = clickedPath[clickedPath.length - 1];
        const deckController = this.deckControllers.get(key);
        const interactionGeneration = deckController?.suspend();
        const parentNode = clickedPath.length > 1 ? clickedPath[clickedPath.length - 2] : null;
        const siblings = parentNode?.children || [clickedNode];
        const closingLane = !!clickedNode.children?.length && clickedNode.collapsed === false;
        const exitingSymbols = closingLane
            ? captureMeterTreeCollapsingLane(this.chartInstances.get(key), clickedNode)
            : [];
        if (clickedNode.children?.length) {
            siblings.forEach(s => {
                if (s !== clickedNode) this.collapseNode(s);
            });
        }
        clickedNode.collapsed = clickedNode.collapsed === undefined ? false : !clickedNode.collapsed;
        const openingLane = !!clickedNode.children?.length && clickedNode.collapsed === false;
        selectMeterTreePath(originalTree, openingLane ? clickedPath : clickedPath.slice(0, -1));
        clickedNode.active = openingLane;
        this.updateNodeVisuals(originalTree, originalTree.value);
        this.treeData.set(key, originalTree);
        const chart = this.chartInstances.get(key);
        if (chart) {
            if (interactionGeneration !== undefined) {
                resumeMeterTreeInteractionsAfterUpdate(chart, deckController, interactionGeneration);
            }
            const layout = responsiveTreeLayout(chart.getWidth(), chart.getHeight(), originalTree);
            const option = this.chartOptions.get(key);
            const responsiveSeries = Array.isArray(option?.series) ? option.series[0] : option?.series;
            updateResponsiveTreeLabelWidth(responsiveSeries, layout.symbolSize[0]);
            if (closingLane) setMeterTreeGraphicsSilent(chart, true);
            chart.setOption({
                animationDurationUpdate: METER_TREE_LANE_ANIMATION_DURATION,
                animationEasingUpdate: METER_TREE_LANE_ANIMATION_EASING,
                series: [{
                    type: 'tree', ...layout, data: [originalTree],
                    animationDurationUpdate: METER_TREE_LANE_ANIMATION_DURATION,
                    animationEasingUpdate: METER_TREE_LANE_ANIMATION_EASING
                }]
            });
            // setOption may refresh the surviving parent symbol and reset its
            // ZRender flags. Keep that new symbol non-interactive too until the
            // collapse has fully finished; exiting children were silenced above.
            if (closingLane) setMeterTreeGraphicsSilent(chart, true);
            if (closingLane) animateMeterTreeCollapsedLane(chart, exitingSymbols);
            if (clickedNode.active) animateMeterTreeExpandedLane(chart, clickedNode);
            keepMeterTreeCardsInDeck(chart);
        } else {
            const newOption = this.createOptionForTree(originalTree);
            this.chartOptions.set(key, newOption);
        }
    }

    collapseNode(node: any) {
        node.collapsed = true;
        node.active = false;
        if (!node.children || node.children.length === 0) return;
        node.children.forEach(child => {
            this.collapseNode(child);
        });
    }

    findNodePath(nodes: MeterTreeNode[], key: string): MeterTreeNode[] | null {
        for (const node of nodes) {
            if (node.id === key || node.entityId === key || node.connectionId === key || node.name === key) return [node];
            if (node.children?.length) {
                const found = this.findNodePath(node.children, key);
                if (found) return [node, ...found];
            }
        }
        return null;
    }

    get rootShareLabel(): string {
        return TranslateDirective.getTranslation(this.treeData.get(this.selectedTreeKey)?.sourceConnectionIds
            ? 'Share of total' : 'Share of main meter', true) as string;
    }

    get treeOptions() {
        return Array.from(this.treeData.keys());
    }

    treeOptionFormatter = (connectionId: string): string => this.treeData.get(connectionId)?.name || connectionId;

    onTreeChanged(newKey: string) {
        this.selectedTreeKey = newKey;
    }

    downloadVisibleSubmeterData() {
        const selectedTree = this.selectedTreeKey ? this.treeData.get(this.selectedTreeKey) : null;
        const connectionIds = selectedTree?.sourceConnectionIds
            || (selectedTree?.connectionId ? [selectedTree.connectionId] : null);
        const visibleMeterIds = (this.meters || []).filter(m => !connectionIds || connectionIds.includes(m.connection?.connectionId))
            .map(m => m.meter?.meterId).filter((id): id is string => !!id);
        if (!visibleMeterIds.length) {
            AppCommonUtils.registerError('No intermediate meters are selected for download.');
            return;
        }
        downloadSubmeterData(this.sourceProvider, this.filters.timeRange, visibleMeterIds);
    }
}
